import { useEffect, useState, useRef } from "react";
import jsPDF from "jspdf";
import { api } from "@/lib/api";
import { PageHeader, StatusBadge, EmptyState, money, fmtDate } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { Plus, Trash, DownloadSimple, MagnifyingGlass, Eye, ChartLineUp } from "@phosphor-icons/react";

const STATUSES = ["draft", "sent", "received"];
const emptyItem = () => ({ name: "", sku: "", quantity: 1, unit_price: 0 });

export default function PurchaseOrders() {
  const navigate = useNavigate();
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [suppliers, setSuppliers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const [supplierId, setSupplierId] = useState("");
  const [orderId, setOrderId] = useState("none");
  const [items, setItems] = useState([emptyItem()]);
  const [notes, setNotes] = useState("");
  const [expected, setExpected] = useState("");
  const [itemCosts, setItemCosts] = useState({});
  const [supplierFilter, setSupplierFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [viewPo, setViewPo] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [avgOpen, setAvgOpen] = useState(false);
  const [avgRows, setAvgRows] = useState(null);
  const [avgSearch, setAvgSearch] = useState("");

  const openAverages = () => {
    setAvgOpen(true);
    if (!avgRows) api.get("/item-averages").then((r) => setAvgRows(r.data.items || [])).catch(() => toast.error("Could not load averages"));
  };

  const load = () => api.get("/purchase-orders").then((r) => setPos(r.data)).finally(() => setLoading(false));
  useEffect(() => {
    load();
    api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/item-costs").then((r) => setItemCosts(r.data || {})).catch(() => {});
  }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/purchase-orders/${id}`, { status });
      toast.success("PO status updated");
      load();
    } catch { toast.error("Update failed"); }
  };

  const removePo = async (id, number) => {
    await api.delete(`/purchase-orders/${id}`);
    toast.success(`PO ${number} deleted`);
    load();
  };

  const openView = async (p) => {
    try {
      const { data } = await api.get(`/purchase-orders/${p.id}`);
      setViewPo(data);
      setViewOpen(true);
    } catch { toast.error("Could not load purchase order"); }
  };

  const downloadPoPdf = (po) => {
    const doc = new jsPDF();
    let y = 20;
    doc.setFont("helvetica", "bold"); doc.setFontSize(20);
    doc.text("Heliopolis Pharmaceuticals", 20, y);
    doc.setFontSize(10); doc.setFont("helvetica", "normal");
    doc.text("ops@heliopolispharmaceuticals.com", 20, (y += 6));
    doc.setFont("helvetica", "bold"); doc.setFontSize(16);
    doc.text(`PURCHASE ORDER ${po.po_number}`, 20, (y += 16));
    doc.setFont("helvetica", "normal"); doc.setFontSize(10);
    doc.text(`Supplier: ${po.supplier_name}`, 20, (y += 7));
    if (po.supplier?.email) doc.text(`Email: ${po.supplier.email}`, 20, (y += 6));
    if (po.supplier?.country) doc.text(`Country: ${po.supplier.country}`, 20, (y += 6));
    doc.text(`Expected: ${fmtDate(po.expected_date)}`, 20, (y += 6));
    doc.text(`Status: ${String(po.status).toUpperCase()}`, 20, (y += 6));
    y += 10;
    doc.setFont("helvetica", "bold");
    doc.text("Product", 20, y); doc.text("Qty", 120, y); doc.text("Cost", 145, y); doc.text("Amount", 175, y);
    doc.line(20, y + 2, 195, y + 2);
    doc.setFont("helvetica", "normal"); y += 8;
    (po.items || []).forEach((it) => {
      doc.text(String(it.name).slice(0, 50), 20, y); doc.text(String(it.quantity), 120, y);
      doc.text(money(it.unit_price), 145, y); doc.text(money(it.quantity * it.unit_price), 175, y);
      y += 7;
    });
    doc.line(20, y, 195, y); y += 8;
    doc.setFont("helvetica", "bold");
    doc.text("Total", 145, y); doc.text(money(po.total), 175, y);
    if (po.notes) { y += 12; doc.setFont("helvetica", "normal"); doc.text(`Notes: ${po.notes}`, 20, y); }
    doc.save(`${po.po_number}.pdf`);
  };

  const updateItem = (i, field, val) => setItems((p) => p.map((it, idx) => (idx === i ? { ...it, [field]: val } : it)));
  const addItem = () => setItems((p) => [...p, emptyItem()]);
  const removeItem = (i) => setItems((p) => p.filter((_, idx) => idx !== i));

  const onPickOrder = (val) => {
    setOrderId(val);
    if (val !== "none") {
      const o = orders.find((x) => x.id === val);
      if (o?.items?.length) setItems(o.items.map((it) => {
        const key = (it.sku || it.name || "").trim().toLowerCase();
        return { ...it, unit_price: itemCosts[key] ?? 0 };
      }));
    }
  };

  const total = items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);

  const pq = search.trim().toLowerCase();
  const filteredPos = pos.filter((p) => {
    if (supplierFilter !== "all" && p.supplier_id !== supplierFilter) return false;
    if (statusFilter !== "all" && p.status !== statusFilter) return false;
    if (pq && !((p.po_number || "").toLowerCase().includes(pq) || (p.items || []).some((it) => (it.name || "").toLowerCase().includes(pq)))) return false;
    return true;
  });

  const resetForm = () => {
    setSupplierId(""); setOrderId("none"); setItems([emptyItem()]); setNotes(""); setExpected(""); setEditId(null);
  };

  const openEdit = (p) => {
    setEditId(p.id);
    setSupplierId(p.supplier_id);
    setOrderId(p.order_id || "none");
    setItems(p.items?.length ? p.items.map((it) => ({ name: it.name, sku: it.sku || "", quantity: it.quantity, unit_price: it.unit_price })) : [emptyItem()]);
    setNotes(p.notes || "");
    setExpected(p.expected_date ? p.expected_date.slice(0, 10) : "");
    setOpen(true);
  };

  const submit = async () => {
    if (!supplierId) return toast.error("Select a supplier");
    const valid = items.filter((it) => it.name.trim());
    if (valid.length === 0) return toast.error("Add at least one line item");
    setBusy(true);
    const payloadItems = valid.map((it) => ({ name: it.name, sku: it.sku, quantity: Number(it.quantity) || 1, unit_price: Number(it.unit_price) || 0 }));
    try {
      if (editId) {
        await api.patch(`/purchase-orders/${editId}/edit`, {
          supplier_id: supplierId, items: payloadItems, notes, expected_date: expected || null,
        });
        toast.success("Purchase order updated");
      } else {
        await api.post("/purchase-orders", {
          supplier_id: supplierId,
          order_id: orderId === "none" ? null : orderId,
          items: payloadItems,
          notes,
          expected_date: expected || null,
        });
        toast.success("Purchase order created");
      }
      setOpen(false);
      resetForm();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const topRef = useRef(null);
  const bodyRef = useRef(null);
  const [scrollW, setScrollW] = useState(0);
  useEffect(() => { if (bodyRef.current) setScrollW(bodyRef.current.scrollWidth); }, [pos]);
  const syncTop = () => { if (bodyRef.current && topRef.current) bodyRef.current.scrollLeft = topRef.current.scrollLeft; };
  const syncBody = () => { if (bodyRef.current && topRef.current) topRef.current.scrollLeft = bodyRef.current.scrollLeft; };

  return (
    <div className="space-y-6">
      <PageHeader
        subtitle="Supplier procurement"
        title="Purchase Orders"
        actions={<div className="flex gap-2">
          <Button variant="outline" className="rounded-sm" onClick={openAverages} data-testid="avg-price-button"><ChartLineUp size={16} className="mr-2" /> Avg purchase price</Button>
          <Button className="rounded-sm" onClick={() => { resetForm(); setOpen(true); }} data-testid="create-po-button"><Plus size={16} className="mr-2" /> Create PO</Button>
        </div>}
      />
      {!loading && pos.length === 0 ? (
        <EmptyState title="No purchase orders" hint="Create a PO to procure goods from a supplier, then advance its status over time." action={<Button className="rounded-sm" onClick={() => setOpen(true)}>Create PO</Button>} />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9 rounded-sm" placeholder="Search by PO number or item name…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="po-search" />
            </div>
            <Select value={supplierFilter} onValueChange={setSupplierFilter}>
              <SelectTrigger className="rounded-sm w-56" data-testid="po-supplier-filter"><SelectValue placeholder="All suppliers" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All suppliers</SelectItem>
                {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="rounded-sm w-40" data-testid="po-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="po-status-filter-all">All statuses</SelectItem>
                {STATUSES.map((st) => <SelectItem key={st} value={st} data-testid={`po-status-filter-${st}`} className="capitalize">{st}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-0">
          <div ref={topRef} onScroll={syncTop} className="overflow-x-auto border border-b-0 border-border rounded-t-md bg-card" data-testid="po-top-scroll"><div style={{ width: scrollW, height: 1 }} /></div>
          <div ref={bodyRef} onScroll={syncBody} className="border border-border rounded-b-md overflow-x-auto bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider font-semibold text-muted-foreground bg-muted/40">
                <th className="px-4 py-3 font-semibold">PO</th>
                <th className="px-4 py-3 font-semibold">Supplier</th>
                <th className="px-4 py-3 font-semibold">Linked Order</th>
                <th className="px-4 py-3 font-semibold">Items</th>
                <th className="px-4 py-3 font-semibold">Total</th>
                <th className="px-4 py-3 font-semibold">Products</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPos.map((p, i) => (
                <tr key={p.id} className={`hover:bg-primary/5 transition-colors ${i % 2 ? "bg-muted/30" : ""}`} data-testid={`po-row-${p.po_number}`}>
                  <td className="px-4 py-3 font-medium">
                    <button onClick={() => navigate(`/purchase-orders/${p.id}`)} className="text-primary hover:underline" data-testid={`po-link-${p.po_number}`}>{p.po_number}</button>
                  </td>
                  <td className="px-4 py-3">{p.supplier_name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{p.order_id ? "linked" : "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{p.items?.length || 0}</td>
                  <td className="px-4 py-3">{money(p.total)}</td>
                  <td className="px-4 py-3 text-muted-foreground" data-testid={`po-products-${p.po_number}`}>
                    {(() => {
                      const names = (p.items || []).map((it) => it.name).filter(Boolean);
                      if (!names.length) return "—";
                      return (
                        <div className="max-w-[220px] text-xs">
                          {names.slice(0, 2).map((n, idx) => <div key={idx} className="truncate">{n}</div>)}
                          {names.length > 2 && <div className="text-[10px] opacity-70">+{names.length - 2} more</div>}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <StatusBadge status={p.status} />
                      <Select value={p.status} onValueChange={(v) => setStatus(p.id, v)}>
                        <SelectTrigger className="rounded-sm h-8 w-28" data-testid={`po-status-${p.po_number}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => openView(p)} data-testid={`view-po-${p.po_number}`} title="View PO">
                        <Eye size={16} className="mr-1" /> View
                      </Button>
                      <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => openEdit(p)} data-testid={`edit-po-${p.po_number}`} title="Edit PO">
                        Edit
                      </Button>
                      <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => navigate(`/purchase-orders/${p.id}`)} data-testid={`export-po-${p.po_number}`} title="View / Export PO">
                        <DownloadSimple size={16} />
                      </Button>
                      <ConfirmDelete
                        testid={`delete-po-${p.po_number}`}
                        title={`Delete ${p.po_number}?`}
                        description="This permanently removes the purchase order. This cannot be undone."
                        onConfirm={() => removePo(p.id, p.po_number)}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          </div>
        </>
      )}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editId ? "Edit Purchase Order" : "Create Purchase Order"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wide">Supplier *</Label>
                <Select value={supplierId} onValueChange={setSupplierId}>
                  <SelectTrigger className="mt-1.5 rounded-sm" data-testid="po-form-supplier"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                  <SelectContent>
                    {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide">Link to order (optional)</Label>
                <Select value={orderId} onValueChange={onPickOrder} disabled={!!editId}>
                  <SelectTrigger className="mt-1.5 rounded-sm" data-testid="po-form-order"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {orders.map((o) => <SelectItem key={o.id} value={o.id}>{o.order_number} — {o.client_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border border-border rounded-md overflow-hidden">
              <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-muted/50 text-xs uppercase tracking-wide font-semibold text-muted-foreground">
                <div className="col-span-5">Item</div>
                <div className="col-span-2">SKU</div>
                <div className="col-span-2">Qty</div>
                <div className="col-span-2">Cost price</div>
                <div className="col-span-1" />
              </div>
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 items-center">
                  <Input className="col-span-5 rounded-sm h-9" placeholder="Item" value={it.name} onChange={(e) => updateItem(i, "name", e.target.value)} data-testid={`po-item-name-${i}`} />
                  <Input className="col-span-2 rounded-sm h-9" placeholder="SKU" value={it.sku} onChange={(e) => updateItem(i, "sku", e.target.value)} />
                  <Input type="number" className="col-span-2 rounded-sm h-9" value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} data-testid={`po-item-qty-${i}`} />
                  <Input type="number" step="0.01" className="col-span-2 rounded-sm h-9" value={it.unit_price} onChange={(e) => updateItem(i, "unit_price", e.target.value)} data-testid={`po-item-price-${i}`} />
                  <button className="col-span-1 flex justify-center text-muted-foreground hover:text-destructive" onClick={() => removeItem(i)} disabled={items.length === 1}><Trash size={16} /></button>
                </div>
              ))}
              <div className="px-3 py-2 border-t border-border">
                <Button variant="outline" size="sm" className="rounded-sm" onClick={addItem} data-testid="po-add-item"><Plus size={14} className="mr-1" /> Add line</Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wide">Expected date</Label>
                <Input type="date" className="mt-1.5 rounded-sm" value={expected} onChange={(e) => setExpected(e.target.value)} data-testid="po-expected" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide">Notes</Label>
                <Textarea rows={1} className="mt-1.5 rounded-sm" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="po-notes" />
              </div>
            </div>

            <div className="flex justify-between items-center border-t border-border pt-3">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">PO Total (cost)</span>
              <span className="font-heading text-xl font-extrabold" data-testid="po-total">{money(total)}</span>
            </div>
            <p className="text-xs text-muted-foreground -mt-2">Cost/purchase prices — separate from client sales prices. Prefilled from the linked order but editable.</p>
          </div>
          <DialogFooter><Button className="rounded-sm" onClick={submit} disabled={busy} data-testid="submit-po">{busy ? "Saving…" : (editId ? "Save changes" : "Create PO")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-2xl" aria-describedby={undefined} data-testid="po-view-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              Purchase Order {viewPo?.po_number}
              {viewPo && <StatusBadge status={viewPo.status} />}
            </DialogTitle>
          </DialogHeader>
          {viewPo && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Supplier</p>
                  <p className="font-medium mt-0.5" data-testid="po-view-supplier">{viewPo.supplier_name}</p>
                  {viewPo.supplier?.email && <p className="text-muted-foreground text-xs">{viewPo.supplier.email}</p>}
                  {viewPo.supplier?.country && <p className="text-muted-foreground text-xs">{viewPo.supplier.country}</p>}
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Expected</p>
                  <p className="font-medium mt-0.5">{fmtDate(viewPo.expected_date)}</p>
                  {viewPo.order_id && <p className="text-muted-foreground text-xs">Linked to an order</p>}
                </div>
              </div>

              <div className="border border-border rounded-md overflow-hidden">
                <div className="grid grid-cols-12 px-3 py-2 bg-muted/50 text-xs uppercase tracking-wide font-semibold text-muted-foreground">
                  <div className="col-span-6">Product</div>
                  <div className="col-span-2 text-right">Qty</div>
                  <div className="col-span-2 text-right">Cost</div>
                  <div className="col-span-2 text-right">Amount</div>
                </div>
                {(viewPo.items || []).map((it, i) => (
                  <div key={i} className="grid grid-cols-12 px-3 py-2 text-sm border-t border-border" data-testid={`po-view-item-${i}`}>
                    <div className="col-span-6 font-medium">{it.name}{it.sku ? <span className="text-muted-foreground text-xs"> · {it.sku}</span> : null}</div>
                    <div className="col-span-2 text-right">{it.quantity}</div>
                    <div className="col-span-2 text-right">{money(it.unit_price)}</div>
                    <div className="col-span-2 text-right">{money(it.quantity * it.unit_price)}</div>
                  </div>
                ))}
              </div>

              <div className="flex justify-end">
                <div className="w-56 flex justify-between border-t border-border pt-2 font-heading font-extrabold text-lg">
                  <span>Total (cost)</span><span data-testid="po-view-total">{money(viewPo.total)}</span>
                </div>
              </div>

              {viewPo.notes && <p className="text-sm text-muted-foreground border-t border-border pt-3"><span className="font-semibold">Notes:</span> {viewPo.notes}</p>}

              {viewPo.audit?.length > 0 && (
                <div className="border-t border-border pt-3" data-testid="po-view-audit">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Edit history</p>
                  <ul className="space-y-1.5 max-h-32 overflow-auto">
                    {[...viewPo.audit].reverse().map((a, i) => (
                      <li key={i} className="text-xs flex items-center justify-between gap-3">
                        <span>{a.action}</span>
                        <span className="text-muted-foreground">{a.by} · {new Date(a.ts).toLocaleString()}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => viewPo && downloadPoPdf(viewPo)} data-testid="po-view-export"><DownloadSimple size={16} className="mr-2" /> Export PDF</Button>
            <Button className="rounded-sm" onClick={() => setViewOpen(false)} data-testid="po-view-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={avgOpen} onOpenChange={setAvgOpen}>
        <DialogContent className="max-w-2xl" data-testid="avg-price-dialog">
          <DialogHeader><DialogTitle>Average purchase price per item</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">Quantity-weighted average across every purchase order line.</p>
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9 rounded-sm" placeholder="Filter by item name or SKU…" value={avgSearch} onChange={(e) => setAvgSearch(e.target.value)} data-testid="avg-price-search" />
          </div>
          <div className="max-h-[55vh] overflow-y-auto border border-border rounded-sm">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-muted/70 backdrop-blur">
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2 font-semibold">Item</th>
                  <th className="px-3 py-2 font-semibold text-right">Avg price</th>
                  <th className="px-3 py-2 font-semibold text-right">Range</th>
                  <th className="px-3 py-2 font-semibold text-right">Qty bought</th>
                  <th className="px-3 py-2 font-semibold text-right">PO lines</th>
                </tr>
              </thead>
              <tbody>
                {avgRows === null ? (
                  <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">Loading…</td></tr>
                ) : (() => {
                  const q = avgSearch.trim().toLowerCase();
                  const filtered = avgRows.filter((r) => !q || `${r.name} ${r.sku}`.toLowerCase().includes(q));
                  if (filtered.length === 0) return <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">No items found.</td></tr>;
                  return filtered.map((r, i) => (
                    <tr key={i} className={`border-t border-border ${i % 2 ? "bg-muted/20" : ""}`} data-testid={`avg-row-${i}`}>
                      <td className="px-3 py-2"><span className="font-medium">{r.name || "—"}</span>{r.sku ? <span className="text-xs text-muted-foreground ml-1">({r.sku})</span> : null}</td>
                      <td className="px-3 py-2 text-right font-semibold text-primary">{money(r.avg_price)}</td>
                      <td className="px-3 py-2 text-right text-muted-foreground text-xs">{r.min_price === r.max_price ? money(r.avg_price) : `${money(r.min_price)}–${money(r.max_price)}`}</td>
                      <td className="px-3 py-2 text-right">{r.total_qty}</td>
                      <td className="px-3 py-2 text-right text-muted-foreground">{r.po_lines}</td>
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
